import React, { useEffect, useState, useMemo } from 'react';
import Cal, { getCalApi } from '@calcom/embed-react';
import { useAppDispatch, useAppSelector } from 'app/helpers/hooks';
import { selectCustomer } from 'app/selectors/customer';
import { apiRequestUserCommand } from 'app/actions/customer';
import { AvailableProducts } from 'app/constants/Products';
import { CallHistoryMethodAction } from 'connected-react-router';
import { List } from 'immutable';
import Spinner from 'app/components/customer/Spinner';
import { PageWrapper } from 'mui';
import { useLiveChat } from 'app/helpers/intercomHelpers';
import { API_REQUEST_USER_COMMAND } from 'app/actions/customer';
import { push } from 'connected-react-router/immutable';
import * as Routes from 'app/constants/Routes';
import { useLocation } from 'react-router-dom';

type Props = {
  onSuccessActionCreator?: (_: any, stateAfterSuccess: any) => CallHistoryMethodAction<[string, any?]>;
  setRescheduleCompleted?: any;
};

const BookingErrMsg = () => {
  const dispatch = useAppDispatch();
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] bg-red-50 border border-red-300 p-6 rounded-lg shadow-md">
      <h5 className="text-xl font-semibold text-red-600">Something went wrong</h5>
      <p className="text-gray-700 mt-2">Try again or contact support for help.</p>
      <button
        className="mt-4 px-4 py-2 bg-red-500 text-white font-semibold rounded-lg shadow-md hover:bg-red-600 transition"
        onClick={() => dispatch(push(Routes.ScheduleAppointment))}
      >
        Try Again
      </button>
    </div>
  );
};

const ScheduleAppointmentForm = ({ onSuccessActionCreator, setRescheduleCompleted }: Props) => {
  const customer = useAppSelector(selectCustomer);
  const physician = useMemo(() => customer.get('physician')?.toJS(), [customer]);
  const firstName = customer.get('first_name');
  const email = customer.get('email');
  const elation_patient_id = customer.get('elation_patient_id');
  const dispatch = useAppDispatch();
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const isUpdatesPending = useMemo(
    () => customer.get('flags', List()).includes('sensitive_updates_pending'),
    [customer],
  );
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isBookingError = queryParams.get('bookingError') === 'true';

  const onSubmitSuccess = (_, stateAfterSuccess) => {
    setSubmitInProgress(false);
    if (onSuccessActionCreator) {
      onSuccessActionCreator(_, stateAfterSuccess);
    }
  };

  const resetBookingListener = async () => {
    const cal = await getCalApi();

    const scheduleAppointment = (e) => {
      if (submitInProgress) return;

      setSubmitInProgress(true);
      // `data` is properties for the event.
      // `type` is the name of the action
      // `namespace` is the Cal namespace for which the event is fired

      const { data } = e.detail;
      handleSubmit(data);
    };

    const rescheduleAppointment = (e) => {
      if (submitInProgress) return;

      setSubmitInProgress(true);

      const { data } = e.detail;
      handleSubmit(data);
      setRescheduleCompleted(true);
    };

    cal('on', {
      action: 'bookingSuccessful',
      callback: scheduleAppointment,
    });
    cal('on', {
      action: 'rescheduleBookingSuccessful',
      callback: rescheduleAppointment,
    });

    return () => {
      cal('off', { action: 'bookingSuccessful', callback: scheduleAppointment });
      cal('off', { action: 'rescheduleBookingSuccessful', callback: rescheduleAppointment });
    };
  };

  const currentAppointment = customer.get('current_appointment');
  const currentAppointmentId = currentAppointment?.get('booking_uid');

  const cmdType = currentAppointmentId ? 'reschedule_appointment' : 'create_appointment';

  useEffect(() => {
    let cleanup;
    (async () => {
      cleanup = await resetBookingListener();
    })();

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  const handleSubmit = (appointmentData) => {
    if (currentAppointmentId) {
      appointmentData.booking_uid = currentAppointmentId;
    }

    const failureHandler = (_res) => {
      return {
        type: API_REQUEST_USER_COMMAND,
        cmdType: 'cancel_calcom_appointment',
        params: {
          appointment_uid: appointmentData.booking.uid,
        },
        context: {
          onSuccessActionCreator: (_, _stateAfterSuccess) =>
            push({
              pathname: Routes.ScheduleAppointment,
              search: '?bookingError=true',
            }),
        },
      };
    };

    const action = apiRequestUserCommand({
      cmdType: cmdType,
      params: {
        product_name: AvailableProducts.King,
        data: JSON.stringify(appointmentData),
      },
      context: {
        onSubmitSuccess,
        onServerFailureActionCreator: cmdType === 'create_appointment' ? (res) => failureHandler(res) : null,
      },
    });

    dispatch(action);
  };

  const providersTeamName = customer.get('customer_calcom_provider_team_name');
  const calcomTeamsEnabled = customer.get('calcom_teams_enabled');
  const physicianAssignedByAdmin = customer.get('physician_assigned_by_admin');

  const caldotcomUrl = currentAppointmentId ? `reschedule/${currentAppointmentId}` : physician?.caldotcom_url;
  const caldotcomTeamUrl = currentAppointmentId ? `reschedule/${currentAppointmentId}` : `/team/${providersTeamName}`;

  const { openLiveChat } = useLiveChat();

  const conciergeDisclaimer = () => {
    return (
      <p>
        Not seeing any times that work with your schedule? Contact our concierge team:
        <br />
        Chat:{' '}
        <button className="link" onClick={openLiveChat}>
          Click here to open
        </button>
        <br />
        Email:{' '}
        <a target="_blank" href="mailto:support@maximustribe.com" rel="noreferrer">
          support@maximustribe.com
        </a>
      </p>
    );
  };

  const getCalComponent = (calLink: string) => (
    <Cal
      calLink={calLink}
      style={{ width: '100%', height: '100%', overflow: 'scroll' }}
      config={{
        layout: 'month_view',
        name: firstName,
        email: email,
      }}
    />
  );

  return (
    <PageWrapper wrapperClassName="flex flex-col gap-y-6 lg:gap-y-8">
      <h3 className="ml-10 sm:ml-5">{currentAppointmentId ? 'Reschedule Appointment' : 'Schedule Appointment'}</h3>
      {conciergeDisclaimer()}
      <div className="content">
        {isUpdatesPending ? (
          <Spinner isCenter />
        ) : !isBookingError && calcomTeamsEnabled && !physicianAssignedByAdmin ? (
          getCalComponent(caldotcomTeamUrl)
        ) : !isBookingError && caldotcomUrl && elation_patient_id ? (
          getCalComponent(caldotcomUrl)
        ) : (
          <BookingErrMsg />
        )}
      </div>
    </PageWrapper>
  );
};

export default ScheduleAppointmentForm;
